import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '@navfleet/client/core/local-storage';
import { FleetFilterService } from '@navfleet/client/fleet/domain/data-access';
import { FleetEventFacade } from '@navfleet/client/fleet/event/domain/data-access';
import { NOTIFICATIONS_SEEN_STORAGE_KEY } from '@navfleet/client/fleet/event/domain/models';
import { SettingsNotificationRuleFacade } from '@navfleet/client/settings/notification/domain/data-access';
import {
    NotificationConfigData,
    NotificationConfigRuleCategoryName,
    NotificationRulesCollection,
} from '@navfleet/client/shared/models/notification';
import { Vessel } from '@navfleet/client/shared/models/vessel';
import { shareLast } from '@navfleet/client/shared/util/functions';
import { LayoutNotification } from '@navfleet/client/shell/domain/models';
import { VesselFacade } from '@navfleet/client/vessel/domain/data-access';
import { countBy, mapValues } from 'lodash';
import { Observable, combineLatest, filter, map, of, switchMap, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LayoutNotificationsService {
    private readonly notificationEventFacade = inject(FleetEventFacade);
    private readonly settingsNotificationRuleFacade = inject(SettingsNotificationRuleFacade);
    private readonly vesselFacade = inject(VesselFacade);
    private readonly fleetFilterService = inject(FleetFilterService);
    private readonly localStorageService = inject(LocalStorageService);

    private readonly seenNotificationIds$ = this.localStorageService.getWithDefault<number[]>(
        NOTIFICATIONS_SEEN_STORAGE_KEY,
        []
    );

    private vesselsMap$: Observable<{ [id: number]: Vessel }> = this.vesselFacade.navFleetVessels$.pipe(
        map((vessels) => vessels.reduce((acc, current) => ({ ...acc, [current.id]: current }), {}))
    );

    private rules$: Observable<NotificationRulesCollection> =
        this.settingsNotificationRuleFacade.getNotificationRules();

    private config$: Observable<NotificationConfigData | null> =
        this.settingsNotificationRuleFacade.getNotificationRuleConfig();

    private allFleetNotifications$: Observable<LayoutNotification[]> = combineLatest([
        this.notificationEventFacade.activeEvents$,
        this.rules$,
        this.config$,
        this.vesselsMap$,
        this.seenNotificationIds$,
    ]).pipe(
        filter(
            ([events, rules, config, vessels, seenIds]) => !!events && !!rules && !!config && !!vessels && !!seenIds
        ),
        map(([events, rules, config, vessels, seenIds]) => {
            const currentTime = new Date();
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(currentTime.getDate() - 7); // 7 days ago

            return (
                events
                    // filter out notification events that are not linked to a vessel or the vessel is not in the store
                    .filter((event) => event.vesselId && vessels[event.vesselId])
                    // #78996 Limit the events displayed for the past 7 days only.
                    .filter((event) => {
                        const itemDate = new Date(event.onTimeStamp); // Convert onTimeStamp to Date object
                        return itemDate >= sevenDaysAgo;
                    })
                    .map((event) => {
                        const rule = rules[event.notificationRuleId];
                        const type = config?.notificationRuleTypes.find(({ id }) => id === rule?.typeId);
                        const categoryId = config?.notificationRuleTypeCategories.find(
                            (item) => item.notificationRuleTypeId === rule?.typeId
                        )?.notificationRuleCategoryId;
                        const categoryName = config?.notificationRuleCategories.find(
                            (category) => category.id === categoryId
                        )?.name;
                        return {
                            name: rule?.isDefaultRule ? type?.nameTranslationCode || '' : rule?.name,
                            vesselName: vessels[event.vesselId]?.name ?? event.vesselId?.toString(),
                            vesselId: event.vesselId,
                            priorityId: event.priorityId,
                            type: type?.name as string,
                            id: event.id,
                            categoryName: categoryName as NotificationConfigRuleCategoryName,
                            onTimeStamp: event.onTimeStamp,
                            seen: seenIds.includes(event.id),
                        };
                    })
            );
        }),
        map((notifications) =>
            notifications.sort((a, b) => {
                if (!a.seen && b.seen) {
                    return -1;
                }
                if (!b.seen && a.seen) {
                    return 1;
                }
                return new Date(b.onTimeStamp).getTime() - new Date(a.onTimeStamp).getTime();
            })
        )
    );

    private notificationsFilteredByFleet$ = combineLatest([
        this.allFleetNotifications$,
        this.fleetFilterService.filteredVesselIds$,
    ]).pipe(
        map(([notifications, vesselIds]) =>
            notifications.filter((n) => vesselIds.some((vesselId) => n.vesselId === vesselId))
        )
    );

    public readonly fleetNotifications$: Observable<LayoutNotification[]> = this.fleetFilterService.isFiltered$.pipe(
        switchMap((isFiltered) => (isFiltered ? this.notificationsFilteredByFleet$ : this.allFleetNotifications$)),
        shareLast()
    );

    public readonly filteredOutNotificationCount$: Observable<{ [x: string]: number } | null> =
        this.fleetFilterService.isFiltered$.pipe(
            switchMap((isFiltered) =>
                isFiltered
                    ? combineLatest([this.allFleetNotifications$, this.notificationsFilteredByFleet$]).pipe(
                          map(([notifications, filteredNotifications]) => {
                              if (notifications.length === filteredNotifications.length) {
                                  return null;
                              }
                              const originalCount = countBy(notifications, 'priorityId');
                              const filteredCount = countBy(filteredNotifications, 'priorityId');

                              return mapValues(originalCount, (count, key) => count - (filteredCount[key] || 0));
                          })
                      )
                    : of(null)
            )
        );

    public updateSeenNotifications() {
        this.fleetNotifications$
            .pipe(
                take(1),
                map((notifications) => notifications.map((notification) => notification.id))
            )
            .subscribe((ids) => this.localStorageService.set(NOTIFICATIONS_SEEN_STORAGE_KEY, ids));
    }
}
